// Hand-drawn SVG charts for the admin overview (no chart library).
import React, { useId, useMemo } from "react";

const W = 640;
const H = 240;
const PAD = { top: 14, right: 12, bottom: 30, left: 40 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

const niceStep = (raw) => {
  if (raw <= 1) return 1;
  const exp = 10 ** Math.floor(Math.log10(raw));
  const f = raw / exp;
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 5 ? 5 : 10;
  return nice * exp;
};

const scaleFor = (max) => {
  const step = niceStep(Math.max(max, 1) / 4);
  return { step, top: step * 4, ticks: [0, 1, 2, 3, 4].map((i) => i * step) };
};

const shortDate = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

const Axes = ({ ticks, top, days, slot }) => (
  <g className="admin-chart-axes">
    {ticks.map((t) => {
      const y = PAD.top + PLOT_H - (t / top) * PLOT_H;
      return (
        <g key={t}>
          <line x1={PAD.left} x2={W - PAD.right} y1={y} y2={y} className="admin-chart-grid" />
          <text x={PAD.left - 8} y={y + 4} textAnchor="end" className="admin-chart-tick">
            {t}
          </text>
        </g>
      );
    })}
    {days.map((d, i) =>
      // Label every other day so 14 labels don't collide
      i % 2 === (days.length - 1) % 2 ? (
        <text
          key={d.date}
          x={PAD.left + slot * i + slot / 2}
          y={H - 10}
          textAnchor="middle"
          className="admin-chart-tick"
        >
          {shortDate(d.date)}
        </text>
      ) : null
    )}
  </g>
);

/** Grouped bars: one group per day, one bar per series. */
export const GroupedBarChart = ({ days, series, title }) => {
  const titleId = useId();
  const descId = useId();
  const max = useMemo(
    () => Math.max(0, ...days.flatMap((d) => series.map((s) => Number(d[s.key]) || 0))),
    [days, series]
  );
  const { top, ticks } = scaleFor(max);
  const slot = PLOT_W / Math.max(days.length, 1);
  const groupW = slot * 0.7;
  const barW = groupW / series.length;

  const totals = series
    .map((s) => `${days.reduce((sum, d) => sum + (Number(d[s.key]) || 0), 0)} ${s.label.toLowerCase()}`)
    .join(", ");

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="admin-chart-svg"
      role="img"
      aria-labelledby={`${titleId} ${descId}`}
    >
      <title id={titleId}>{title}</title>
      <desc id={descId}>
        {days.length ? `From ${shortDate(days[0].date)} to ${shortDate(days[days.length - 1].date)}: ${totals}.` : "No data."}
      </desc>
      <Axes ticks={ticks} top={top} days={days} slot={slot} />
      {days.map((d, i) => {
        const x0 = PAD.left + slot * i + (slot - groupW) / 2;
        return (
          <g key={d.date}>
            {series.map((s, j) => {
              const v = Number(d[s.key]) || 0;
              const h = (v / top) * PLOT_H;
              return (
                <rect
                  key={s.key}
                  x={x0 + j * barW + 1}
                  y={PAD.top + PLOT_H - h}
                  width={Math.max(barW - 2, 1)}
                  height={Math.max(h, v > 0 ? 1.5 : 0)}
                  rx={2}
                  className={`admin-chart-bar ${s.className}`}
                >
                  <title>{`${shortDate(d.date)}: ${v} ${s.label.toLowerCase()}`}</title>
                </rect>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
};

/** Single-series line with a soft area fill. */
export const LineChart = ({ days, valueKey, label, title }) => {
  const titleId = useId();
  const descId = useId();
  const values = days.map((d) => Number(d[valueKey]) || 0);
  const { top, ticks } = scaleFor(Math.max(0, ...values));
  const slot = PLOT_W / Math.max(days.length, 1);
  const pts = values.map((v, i) => [PAD.left + slot * i + slot / 2, PAD.top + PLOT_H - (v / top) * PLOT_H]);
  const line = pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const baseY = PAD.top + PLOT_H;
  const area = pts.length
    ? `${line} L${pts[pts.length - 1][0].toFixed(1)},${baseY} L${pts[0][0].toFixed(1)},${baseY} Z`
    : "";
  const total = values.reduce((a, b) => a + b, 0);

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="admin-chart-svg"
      role="img"
      aria-labelledby={`${titleId} ${descId}`}
    >
      <title id={titleId}>{title}</title>
      <desc id={descId}>
        {days.length
          ? `From ${shortDate(days[0].date)} to ${shortDate(days[days.length - 1].date)}: ${total} ${label.toLowerCase()} in total.`
          : "No data."}
      </desc>
      <Axes ticks={ticks} top={top} days={days} slot={slot} />
      {area && <path d={area} className="admin-chart-area" />}
      {line && <path d={line} className="admin-chart-line" />}
      {pts.map(([x, y], i) => (
        <circle key={days[i].date} cx={x} cy={y} r={4} className="admin-chart-dot">
          <title>{`${shortDate(days[i].date)}: ${values[i]} ${label.toLowerCase()}`}</title>
        </circle>
      ))}
    </svg>
  );
};

/** Screen-reader table mirroring the chart data. */
export const ChartDataTable = ({ days, columns, caption }) => (
  <table className="sr-only">
    <caption>{caption}</caption>
    <thead>
      <tr>
        <th scope="col">Date</th>
        {columns.map((c) => (
          <th scope="col" key={c.key}>
            {c.label}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {days.map((d) => (
        <tr key={d.date}>
          <th scope="row">{shortDate(d.date)}</th>
          {columns.map((c) => (
            <td key={c.key}>{Number(d[c.key]) || 0}</td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);
